import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
    ArrowRight, ArrowUpRight, BadgeCheck, Gem, Cpu, ScrollText, Clock, Headset, Star, ChevronLeft, ChevronRight,
    FileSearch, ShieldCheck, BadgeEuro, HandCoins, Award, Leaf,
} from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, MaskLine, Counter } from "@/components/site/motion";
import { SERVICES, PROJECTS, TESTIMONIALS, PARTNER_BRANDS, IMAGES } from "@/data/content";
import Seo from "@/components/site/Seo";
import Offices from "@/components/site/Offices";
import CoverageMap from "@/components/site/CoverageMap";

const WHY_ICONS = { certified: BadgeCheck, quality: Gem, innovation: Cpu, compliance: ScrollText, onTime: Clock, aftersales: Headset };

function Hero() {
    const { t } = useLang();
    const navigate = useNavigate();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-cb-darker">
            <motion.div style={{ y, scale }} className="absolute inset-0">
                <img src={IMAGES.hero} alt="Impianti industriali moderni" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-cb-darker/70" />
                <div className="absolute inset-0 bg-gradient-to-t from-cb-darker via-cb-darker/30 to-transparent" />
            </motion.div>
            <div className="absolute inset-0 cb-grid opacity-40" />

            <motion.div style={{ y: overlayY }} className="relative z-10 max-w-7xl mx-auto px-6 min-h-screen flex flex-col justify-center pt-24 pb-32">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3 }}
                    className="flex items-center gap-3 mb-6 text-cb-accent text-xs uppercase tracking-[0.3em]">
                    <span className="h-px w-10 bg-cb-accent" />{t.hero.overline}
                </motion.div>

                <h1 aria-label={`${t.hero.l1} ${t.hero.l2} ${t.hero.l3}`} className="font-display text-white text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.95]">
                    <MaskLine delay={2.4}>{t.hero.l1}</MaskLine>
                    <MaskLine delay={2.55} className="text-cb-accent">{t.hero.l2}</MaskLine>
                    <MaskLine delay={2.7}>{t.hero.l3}</MaskLine>
                </h1>

                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3, duration: 0.8 }}
                    className="mt-8 max-w-2xl text-slate-300 text-lg md:text-xl leading-relaxed">
                    {t.hero.sub}
                </motion.p>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.2, duration: 0.8 }}
                    className="mt-10 flex flex-wrap gap-4">
                    <button data-testid="hero-quote-btn" onClick={() => navigate("/preventivo")}
                        className="cb-glow inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-4 font-medium transition-colors">
                        {t.hero.cta1} <ArrowRight size={18} />
                    </button>
                    <Link data-testid="hero-works-btn" to="/progetti"
                        className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-cb-dark px-8 py-4 font-medium transition-colors">
                        {t.hero.cta2}
                    </Link>
                </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.5 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/60">
                <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
                <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="h-10 w-px bg-white/40" />
            </motion.div>
        </section>
    );
}

function About() {
    const { t } = useLang();
    return (
        <section data-testid="about-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal className="relative">
                    <div className="relative h-[520px] bg-cb-light border border-slate-200 overflow-hidden flex items-center justify-center">
                        <div className="absolute inset-0 cb-grid opacity-60" />
                        <div className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.10),transparent_60%)]" />
                        <motion.img
                            whileHover={{ scale: 1.04 }}
                            transition={{ duration: 0.6 }}
                            src="/brand/logo.png"
                            alt="Logo Gruppo CB"
                            className="relative w-[76%] max-h-[78%] object-contain drop-shadow-[0_20px_40px_rgba(15,23,42,0.15)]"
                        />
                    </div>
                    <div className="absolute -bottom-6 -right-4 sm:right-6 bg-cb-dark text-white px-8 py-6">
                        <div className="font-display text-4xl font-bold text-cb-accent">2001</div>
                        <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Since</div>
                    </div>
                </Reveal>

                <div>
                    <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.about.chapter}</Reveal>
                    <Reveal as="h2" delay={0.5} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-cb-dark">
                        {t.about.title}
                    </Reveal>
                    <Reveal delay={1} className="mt-6 text-slate-600 leading-relaxed">{t.about.p1}</Reveal>
                    <Reveal delay={1.5} className="mt-4 text-slate-600 leading-relaxed">{t.about.p2}</Reveal>

                    <div className="mt-10 grid grid-cols-2 gap-8">
                        {t.about.stats.map((s, i) => (
                            <Reveal key={`${s.value}${s.suffix}`} delay={2 + i * 0.5}>
                                <div className="font-display text-4xl md:text-5xl font-bold text-cb-dark">
                                    <Counter value={s.value} suffix={s.suffix} />
                                </div>
                                <div className="mt-1 text-sm text-slate-500">{s.label}</div>
                                <div className="mt-3 h-px w-12 bg-cb-accent" />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const ESCO_ICONS = [FileSearch, ShieldCheck, BadgeEuro, HandCoins];

function EscoSection() {
    const { t } = useLang();
    const navigate = useNavigate();
    const e = t.esco;
    return (
        <section data-testid="esco-section" className="relative py-24 md:py-32 bg-cb-darker text-white overflow-hidden">
            <div className="absolute inset-0 cb-grid opacity-40" />
            {/* Large watermark */}
            <div aria-hidden="true" className="pointer-events-none select-none absolute -top-10 right-0 font-display text-[26vw] leading-none font-bold text-white/[0.03]">
                ESCo
            </div>

            <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <Reveal className="flex items-center gap-4 mb-6">
                        <span className="inline-flex items-center gap-2 border border-cb-accent/40 bg-cb-accent/10 text-cb-accent px-4 py-2 text-xs uppercase tracking-[0.2em]">
                            <Award size={16} strokeWidth={1.6} /> {e.badge}
                        </span>
                        <span className="text-cb-accent text-xs uppercase tracking-[0.3em]">{e.chapter}</span>
                    </Reveal>
                    <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight">
                        {e.title}
                    </Reveal>
                    <Reveal delay={0.8} className="mt-6 text-slate-300 leading-relaxed text-lg max-w-xl">{e.p}</Reveal>
                    <Reveal delay={1.1}>
                        <button
                            data-testid="esco-cta-btn"
                            onClick={() => navigate("/preventivo")}
                            className="cb-glow mt-8 inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-4 font-medium transition-colors"
                        >
                            <Leaf size={18} strokeWidth={1.6} /> {e.cta}
                        </button>
                    </Reveal>
                </div>

                <div className="grid sm:grid-cols-2 gap-px bg-white/10">
                    {e.items.map((it, i) => {
                        const Icon = ESCO_ICONS[i];
                        return (
                            <Reveal key={it.t} delay={i * 0.4} className="group bg-cb-dark p-8 hover:bg-cb-slate transition-colors duration-500">
                                <Icon size={32} strokeWidth={1.3} className="text-cb-accent" />
                                <h3 className="mt-6 font-display text-lg font-medium">{it.t}</h3>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{it.d}</p>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function ServicesSection() {
    const { t, lang } = useLang();
    const items = SERVICES.slice(0, 4);
    return (
        <section data-testid="services-section" className="py-24 md:py-32 bg-cb-light">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.services.chapter}</Reveal>
                        <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-cb-dark">{t.services.title}</Reveal>
                    </div>
                    <Reveal delay={0.6} className="text-slate-600 max-w-sm">{t.services.sub}</Reveal>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
                    {items.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.slug} delay={i * 0.6}>
                                <Link to={`/servizi/${s.slug}`} data-testid={`service-card-${s.slug}`}
                                    className="group block h-full bg-white p-8 hover:bg-cb-dark transition-colors duration-500">
                                    <Icon size={40} strokeWidth={1.2} className="text-cb-accent" />
                                    <h3 className="mt-8 font-display text-xl font-semibold text-cb-dark group-hover:text-white transition-colors">{s[lang].name}</h3>
                                    <p className="mt-3 text-sm text-slate-500 group-hover:text-slate-300 transition-colors leading-relaxed">{s[lang].short}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-cb-accent">
                                        {t.services.more} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    </span>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function WhySection() {
    const { t } = useLang();
    return (
        <section data-testid="why-section" className="relative py-24 md:py-32 bg-cb-dark cb-grid text-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 relative">
                <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.why.chapter}</Reveal>
                <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight max-w-2xl">{t.why.title}</Reveal>

                <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
                    {Object.keys(t.why.items).map((key, i) => {
                        const Icon = WHY_ICONS[key];
                        return (
                            <Reveal key={key} delay={i * 0.4} className="flex items-start gap-5 group">
                                <div className="shrink-0 border border-white/15 p-4 group-hover:border-cb-accent group-hover:bg-cb-accent/10 transition-colors">
                                    <Icon size={30} strokeWidth={1.3} className="text-cb-accent" />
                                </div>
                                <div>
                                    <h3 className="font-display text-lg font-medium">{t.why.items[key]}</h3>
                                    <div className="mt-3 h-px w-8 bg-white/20 group-hover:w-16 group-hover:bg-cb-accent transition-all duration-500" />
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Portfolio() {
    const { t, lang } = useLang();
    const items = PROJECTS.slice(0, 5);
    const spans = ["lg:col-span-8", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];
    return (
        <section data-testid="portfolio-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.portfolio.chapter}</Reveal>
                        <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-cb-dark">{t.portfolio.title}</Reveal>
                    </div>
                    <Reveal delay={0.6}>
                        <Link to="/progetti" className="inline-flex items-center gap-2 text-sm font-medium text-cb-dark hover:text-cb-accent transition-colors">
                            {t.portfolio.view} <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {items.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.3} className={spans[i]}>
                            <Link to="/progetti" data-testid={`portfolio-item-${p.id}`} className="group relative block overflow-hidden h-72 lg:h-80">
                                <img src={p.image} alt={p[lang].name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-cb-darker via-cb-darker/20 to-transparent opacity-90" />
                                <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                                    <span className="text-cb-accent text-xs uppercase tracking-widest">{p[lang].location}</span>
                                    <h3 className="font-display text-xl font-semibold text-white mt-1">{p[lang].name}</h3>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BrandLogo({ brand }) {
    const sources = [
        `https://logo.debounce.com/${brand.domain}`,
        `https://logos.hunter.io/${brand.domain}`,
    ];
    const [idx, setIdx] = useState(0);
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <span className="font-display text-2xl md:text-3xl font-semibold text-slate-400 mx-10 whitespace-nowrap select-none">
                {brand.name}
            </span>
        );
    }
    return (
        <img
            src={sources[idx]}
            alt={brand.name}
            loading="lazy"
            onError={() => (idx < sources.length - 1 ? setIdx(idx + 1) : setFailed(true))}
            className="mx-10 h-10 md:h-12 w-auto object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 select-none"
        />
    );
}

function Clients() {
    const { t } = useLang();
    const row = [...PARTNER_BRANDS, ...PARTNER_BRANDS];
    return (
        <section data-testid="clients-section" className="py-16 bg-cb-light border-y border-slate-200 overflow-hidden">
            <p className="text-center text-xs uppercase tracking-[0.3em] text-slate-400 mb-10">{t.clients.title}</p>
            <div className="cb-marquee-track items-center">
                {row.map((b, i) => (
                    <BrandLogo key={`${b.name}-${i < PARTNER_BRANDS.length ? "a" : "b"}`} brand={b} />
                ))}
            </div>
        </section>
    );
}

function Testimonials() {
    const { t, lang } = useLang();
    const [idx, setIdx] = useState(0);
    const n = TESTIMONIALS.length;
    useEffect(() => {
        const id = setInterval(() => setIdx((i) => (i + 1) % n), 6000);
        return () => clearInterval(id);
    }, [n]);
    const go = (d) => setIdx((i) => (i + d + n) % n);
    const cur = TESTIMONIALS[idx][lang];

    return (
        <section data-testid="testimonials-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-4xl mx-auto px-6 text-center">
                <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.reviews.chapter}</Reveal>
                <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-12">{t.reviews.title}</Reveal>

                <div className="relative min-h-[220px]">
                    <AnimatePresence mode="wait">
                        <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
                            <div className="flex justify-center gap-1 mb-6">
                                {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={k} size={20} className="fill-cb-accent text-cb-accent" />)}
                            </div>
                            <p className="font-display text-2xl md:text-3xl font-light leading-snug text-cb-dark">“{cur.text}”</p>
                            <div className="mt-8">
                                <div className="font-semibold text-cb-dark">{cur.author}</div>
                                <div className="text-sm text-slate-500">{cur.role}</div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="flex items-center justify-center gap-4 mt-10">
                    <button data-testid="testimonial-prev" onClick={() => go(-1)} className="border border-slate-200 p-3 hover:bg-cb-dark hover:text-white transition-colors"><ChevronLeft size={18} /></button>
                    <div className="flex gap-2">
                        {TESTIMONIALS.map((tm, i) => (
                            <button key={tm.it.author} onClick={() => setIdx(i)} className={`h-1.5 transition-all ${i === idx ? "w-8 bg-cb-accent" : "w-1.5 bg-slate-300"}`} />
                        ))}
                    </div>
                    <button data-testid="testimonial-next" onClick={() => go(1)} className="border border-slate-200 p-3 hover:bg-cb-dark hover:text-white transition-colors"><ChevronRight size={18} /></button>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    const { t } = useLang();
    const navigate = useNavigate();
    return (
        <section data-testid="final-cta-section" className="relative py-32 md:py-44 overflow-hidden">
            <div className="absolute inset-0">
                <img src={IMAGES.cta} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-cb-darker/85" />
            </div>
            <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                <Reveal as="h2" className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white">{t.finalCta.title}</Reveal>
                <Reveal delay={0.5} className="mt-6 text-slate-300 text-lg max-w-2xl mx-auto">{t.finalCta.sub}</Reveal>
                <Reveal delay={1}>
                    <button data-testid="final-cta-btn" onClick={() => navigate("/preventivo")}
                        className="cb-glow mt-10 inline-flex items-center gap-3 bg-cb-accent hover:bg-cb-accentHover text-white text-lg px-10 py-5 font-medium transition-colors">
                        {t.finalCta.btn} <ArrowRight size={20} />
                    </button>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    const { t } = useLang();
    return (
        <>
            <Seo title={t.seo.home.t} description={t.seo.home.d} image={IMAGES.hero} path="/" />
            <Hero />
            <About />
            <EscoSection />
            <ServicesSection />
            <WhySection />
            <Portfolio />
            <CoverageMap compact />
            <Clients />
            <Testimonials />
            <Offices />
            <FinalCTA />
        </>
    );
}
